import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ProductModal from "../components/ProductModal";
import ColourFilters from "../components/ColourFilters";
import ColourSwatches from "../components/ColourSwatches";
import CustomDesignNotice from "../components/CustomDesignNotice";
import ProductsInfoSection from "../components/ProductsInfoSection";
import IverOptions from "../components/IverOptions";
import { getColour, DEFAULT_COLOUR_KEY } from "../data/colours";

/**
 * Product taxonomy. Each category owns a code prefix and a placeholder count —
 * blank cards render until real product renders drop in. Add real products
 * later by extending `items` with { name, src, spec } entries.
 */
const CATEGORIES = [
  { key: "steel-windows", label: "Steel Windows", codePrefix: "SW", placeholderCount: 12 },
  { key: "steel-doors", label: "Steel Doors", codePrefix: "SD", placeholderCount: 15 },
  { key: "luxury-entrances", label: "Luxury Entrances", codePrefix: "LE", placeholderCount: 8 },
  { key: "hardware", label: "Hardware", codePrefix: "ES-HW", placeholderCount: 16 },
];

const ease = [0.19, 1, 0.22, 1];

/**
 * Real product renders keyed by category. Slot index (0-based) maps to the
 * placeholder position — e.g. steel-doors[0] fills SD1. Add more entries as
 * renders come in; unfilled positions stay as blank placeholders.
 */
const REAL_PRODUCTS = {
  "steel-windows": [
    {
      name: "4-Lite Casement Window",
      restSrc: "/doors/sw1-rest.webp",
      src: "/doors/sw1-closed.webp",
      hoverSrc: "/doors/sw1-open.webp",
    },
    {
      name: "8-Lite Casement Window",
      restSrc: "/doors/sw2-rest.webp",
      src: "/doors/sw2-closed.webp",
      hoverSrc: "/doors/sw2-open.webp",
    },
    {
      name: "16-Lite Casement Window",
      restSrc: "/doors/sw3-rest.webp",
      src: "/doors/sw3-closed.webp",
      hoverSrc: "/doors/sw3-open.webp",
    },
    {
      name: "Arched-Top Fixed Steel Window",
      restSrc: "/doors/sw4-rest.webp",
      src: "/doors/sw4-closed.webp",
    },
    {
      name: "4-Lite Fixed Steel Window",
      restSrc: "/doors/sw5-rest.webp",
      src: "/doors/sw5-closed.webp",
    },
    {
      name: "Cathedral Arched Fixed Steel Window",
      restSrc: "/doors/sw6-rest.webp",
      src: "/doors/sw6-closed.webp",
    },
    {
      name: "Awning Steel Window",
      restSrc: "/doors/sw7-rest.webp",
      src: "/doors/sw7-closed.webp",
      hoverSrc: "/doors/sw7-open.webp",
    },
    {
      name: "Triple-Section Steel Window with Double Awning",
      restSrc: "/doors/sw8-rest.webp",
      src: "/doors/sw8-closed.webp",
    },
    {
      name: "4-Lite Awning Steel Window",
      restSrc: "/doors/sw9-rest.webp",
      src: "/doors/sw9-closed.webp",
      hoverSrc: "/doors/sw9-open.webp",
    },
    {
      name: "4-Panel Bi-Fold Steel Window",
      restSrc: "/doors/sw10-rest.webp",
      src: "/doors/sw10-closed.webp",
      hoverSrc: "/doors/sw10-open.webp",
    },
    {
      name: "4-Panel Casement Steel Window with Transom",
      restSrc: "/doors/sw11-rest.webp",
      src: "/doors/sw11-closed.webp",
    },
    {
      name: "Arched 4-Panel Casement Steel Window",
      restSrc: "/doors/sw12-rest.webp",
      src: "/doors/sw12-closed.webp",
    },
  ],
  "steel-doors": [
    {
      name: "3-Panel Single Steel Door",
      restSrc: "/doors/sd11-rest.webp",
      src: "/doors/sd11-closed.webp",
      hoverSrc: "/doors/sd11-open.webp",
    },
    {
      name: "8-Lite Single Steel Door",
      restSrc: "/doors/sd10-rest.webp",
      src: "/doors/sd10-closed.webp",
      hoverSrc: "/doors/sd10-open.webp",
    },
    {
      name: "Single Steel Door with Fixed Sidelight",
      restSrc: "/doors/sd12-rest.webp",
      src: "/doors/sd12-closed.webp",
      hoverSrc: "/doors/sd12-open.webp",
    },
    {
      name: "8-Lite Double Steel Door",
      restSrc: "/doors/sd7-rest.webp",
      src: "https://customer-assets-eiarnc6j.emergentagent.net/job_e2af7c5a-ec14-4f81-9265-c8de7479757b/artifacts/mk4rtcts_8%20lite%20door%201.png",
      hoverSrc:
        "https://customer-assets-eiarnc6j.emergentagent.net/job_e2af7c5a-ec14-4f81-9265-c8de7479757b/artifacts/npvp9adp_8%20lite%20door%202.png",
    },
    {
      name: "6-Lite Divider Double Steel Door",
      restSrc: "/doors/sd5-rest.webp",
      src: "https://customer-assets-v7afamib.emergentagent.net/job_3cc12260-5622-4383-abad-481c3375154e/artifacts/0w2y1ch6_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2007_44_51%20PM.png",
      hoverSrc:
        "https://customer-assets-v7afamib.emergentagent.net/job_3cc12260-5622-4383-abad-481c3375154e/artifacts/i4k9emub_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2007_46_18%20PM.png",
    },
    {
      name: "Double French Steel Door — 6-Lite",
      restSrc: "/doors/sd2-rest.webp",
      src: "https://customer-assets-v7afamib.emergentagent.net/job_3cc12260-5622-4383-abad-481c3375154e/artifacts/mid5qmf8_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2006_25_37%20PM.png",
      hoverSrc:
        "https://customer-assets-v7afamib.emergentagent.net/job_3cc12260-5622-4383-abad-481c3375154e/artifacts/okmkb840_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2006_26_56%20PM.png",
    },
    {
      name: "16-Lite Divider Double Steel Door",
      restSrc: "/doors/sd4-rest.webp",
      src: "https://customer-assets-v7afamib.emergentagent.net/job_3cc12260-5622-4383-abad-481c3375154e/artifacts/a158fepp_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2007_02_46%20PM.png",
      hoverSrc:
        "https://customer-assets-v7afamib.emergentagent.net/job_3cc12260-5622-4383-abad-481c3375154e/artifacts/z4h7looa_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2007_04_20%20PM.png",
    },
    {
      name: "Single Steel Door with Twin Sidelights",
      restSrc: "/doors/sd8-rest.webp",
      src: "/doors/sd8-closed.webp",
      hoverSrc: "/doors/sd8-open.webp",
    },
    {
      name: "Double French Steel Door with Transom",
      restSrc: "/doors/sd1-rest.webp",
      src: "/doors/sd1-closed.webp",
      hoverSrc: "/doors/sd1-open.webp",
    },
    {
      name: "Full-Arch Double Steel Door — French",
      restSrc: "/doors/sd9-rest.webp",
      src: "/doors/sd9-closed.webp",
      hoverSrc: "/doors/sd9-open.webp",
      imgScale: 1.08,
    },
    {
      name: "Full-Arch Double Steel Door",
      restSrc: "/doors/sd6-rest.webp",
      src: "https://customer-assets-v7afamib.emergentagent.net/job_3cc12260-5622-4383-abad-481c3375154e/artifacts/00l3aqow_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2005_17_39%20PM.png",
      hoverSrc:
        "https://customer-assets-v7afamib.emergentagent.net/job_3cc12260-5622-4383-abad-481c3375154e/artifacts/uyw4or0y_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2007_54_35%20PM.png",
      imgScale: 1.08,
    },
    {
      name: "Arch-in-Square Double Steel Door",
      restSrc: "/doors/sd3-rest.webp",
      src: "/doors/sd3-closed.webp",
      hoverSrc: "/doors/sd3-open.webp",
    },
    {
      name: "Tri-Panel Bi-Fold Steel Door",
      restSrc: "/doors/sd13-rest.webp",
      src: "/doors/sd13-closed.webp",
      hoverSrc: "/doors/sd13-open.webp",
    },
    {
      name: "Double Steel Door with Sidelights & Transom",
      restSrc: "/doors/sd14-rest.webp",
      src: "/doors/sd14-closed.webp",
      hoverSrc: "/doors/sd14-open.webp",
    },
    {
      name: "Full-Arch Double Steel Door with Sidelights",
      restSrc: "/doors/sd15-rest.webp",
      src: "/doors/sd15-closed.webp",
      hoverSrc: "/doors/sd15-open.webp",
    },
  ],
  "luxury-entrances": [
    {
      name: "Ribbed Double Pivot Entrance",
      restSrc: "/doors/le1-rest.webp",
      src: "/doors/le1-closed.webp",
      hoverSrc: "/doors/le1-open.webp",
    },
    {
      name: "Stone Panel Pivot Entrance",
      restSrc: "/doors/le2-rest.webp",
      src: "/doors/le2-closed.webp",
      hoverSrc: "/doors/le2-open.webp",
    },
    {
      name: "Bronze Patina Pivot Entrance",
      src: "/doors/le3-closed.webp",
      hoverSrc: "/doors/le3-open.webp",
    },
    {
      name: "Arched Ribbed Double Entrance",
      src: "/doors/le4-closed.webp",
      hoverSrc: "/doors/le4-open.webp",
    },
    {
      name: "Horizontal-Panel Double Pivot Entrance",
      src: "/doors/le5-open.webp",
    },
    {
      name: "Fluted Pivot Entrance with Sidelight",
      src: "/doors/le6-closed.webp",
    },
    {
      name: "Fluted Pivot Entrance with Twin Sidelights",
      src: "/doors/le7-closed.webp",
    },
    {
      name: "Horizontal-Panel Pivot Entrance with Twin Sidelights",
      src: "/doors/le8-closed.webp",
    },
  ],
  hardware: [
    { code: "ES-DH01", name: "Brass", src: "/doors/es-dh01.webp", subCategory: "signature" },
    { code: "ES-DH02", name: "Satin Nickel", src: "/doors/es-dh02.webp", subCategory: "signature" },
    { code: "ES-DH03", name: "Brass", src: "/doors/es-dh03.webp", subCategory: "signature" },
    { code: "ES-DH04", name: "Matte Black", src: "/doors/es-dh04.webp", subCategory: "signature" },
    { code: "ES-DH05", name: "Brass", src: "/doors/es-dh05.webp", subCategory: "signature" },
    { code: "ES-WH07", name: "Brass", src: "/doors/es-wh07.webp", subCategory: "signature" },
    { code: "ES-WH08", name: "Brass", src: "/doors/es-wh08.webp", subCategory: "signature" },
    { code: "ES-WH01", name: "Graphite", src: "/doors/es-wh01.webp", subCategory: "signature" },
    { code: "ES-WH03", name: "Antique Brass", src: "/doors/es-wh03.webp", subCategory: "signature" },
    { code: "ES-WH13", name: "Brass", src: "/doors/es-wh13.webp", subCategory: "signature" },
    { code: "ES-WH15", name: "Brass", src: "/doors/es-wh15.webp", subCategory: "signature" },
    {
      code: "IV-ANNECY",
      name: "Annecy Lever",
      src: "/doors/iver-annecy.jpg",
      subCategory: "iver",
      finishes: [
        "Brushed Brass",
        "Signature Brass",
        "Polished Brass",
        "Matt Black",
        "Brushed Chrome",
        "Polished Chrome",
        "Satin Nickel",
      ],
      backplates: ["Stepped", "Chamfered", "Round Rose", "Square Rose"],
    },
    {
      code: "IV-BRUNSWICK",
      name: "Brunswick Lever",
      src: "/doors/iver-brunswick.webp",
      subCategory: "iver",
      finishes: [
        "Signature Brass",
        "Brushed Brass",
        "Polished Brass",
        "Matt Black",
        "Brushed Chrome",
        "Polished Chrome",
        "Satin Nickel",
        "Brushed Gold PVD",
      ],
      backplates: ["Oval", "Shouldered", "Round Rose", "Square Rose"],
    },
    {
      code: "IV-SARLAT",
      name: "Sarlat Lever",
      src: "/doors/iver-sarlat.png",
      subCategory: "iver",
      finishes: [
        "Polished Chrome",
        "Polished Brass",
        "Brushed Brass",
        "Matt Black",
        "Signature Brass",
        "Brushed Chrome",
        "Satin Nickel",
      ],
      backplates: ["Stepped", "Shouldered", "Oval", "Round Rose", "Square Rose"],
    },
    {
      code: "IV-HELSINKI",
      name: "Helsinki Pull Handle",
      src: "/doors/iver-helsinki-pull.jpg",
      subCategory: "iver",
      finishes: [
        "Brushed Gold PVD",
        "Brushed Brass",
        "Signature Brass",
        "Polished Brass",
        "Matt Black",
        "Brushed Chrome",
        "Polished Chrome",
        "Satin Nickel",
      ],
      backplates: ["450mm", "600mm", "900mm"],
    },
    {
      code: "IV-BALTIMORE",
      name: "Baltimore Pull Handle",
      src: "/doors/iver-baltimore.jpg",
      subCategory: "iver",
      finishes: [
        "Brushed Brass",
        "Signature Brass",
        "Polished Brass",
        "Matt Black",
        "Brushed Chrome",
        "Polished Chrome",
        "Satin Nickel",
        "Brushed Gold PVD",
      ],
      backplates: ["600mm", "900mm"],
    },
  ],
};

/** Build every card upfront so the filter is a pure client-side toggle. */
function buildProducts() {
  return CATEGORIES.flatMap((cat) => {
    const reals = REAL_PRODUCTS[cat.key] || [];
    return Array.from({ length: cat.placeholderCount }, (_, i) => {
      const real = reals[i];
      const base = {
        code: `${cat.codePrefix}${i + 1}`,
        categoryKey: cat.key,
        categoryLabel: cat.label,
      };
      if (!real) return { ...base, isPlaceholder: true };
      // Iver hardware items already carry their own code (IV-*); keep it so the
      // human-readable model is displayed instead of the ES-HW slot number.
      return {
        ...base,
        ...real,
        code: real.code || base.code,
        isPlaceholder: false,
      };
    });
  });
}

/** ProductCard — displays a single product tile. */
const ProductCard = ({ product, index, onOpen, colourKey, onColourChange }) => {
  const {
    code,
    categoryKey,
    categoryLabel,
    subCategory,
    name,
    src,
    hoverSrc,
    restSrc,
    isPlaceholder,
    imgScale,
    finishes,
    backplates,
  } = product;
  const scaleStyle = imgScale ? { transform: `scale(${imgScale})` } : undefined;
  const has3State = Boolean(restSrc);
  const hasOpenState = Boolean(hoverSrc);
  const tintable = categoryKey === "steel-doors" || categoryKey === "steel-windows";
  const [previewColourKey, setPreviewColourKey] = useState(null);
  const displayColour = tintable
    ? getColour(previewColourKey || colourKey || DEFAULT_COLOUR_KEY)
    : null;
  const tintStyle = displayColour?.filter
    ? { filter: `url(#${displayColour.filter})` }
    : undefined;
  const [showClosed, setShowClosed] = useState(false);
  const [showOpen, setShowOpen] = useState(false);
  const openTimerRef = useRef(null);
  const handleEnter = () => {
    if (!has3State) return;
    setShowClosed(true);
    if (!hasOpenState) return;
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => setShowOpen(true), 300);
  };
  const handleLeave = () => {
    if (!has3State) return;
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    setShowClosed(false);
    setShowOpen(false);
  };
  useEffect(() => () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
  }, []);
  const clickable = !isPlaceholder && Boolean(onOpen);
  const isHardware = categoryKey === "hardware";
  const handleClick = () => {
    if (clickable) onOpen(product);
  };
  const handleKey = (e) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen(product);
    }
  };
  return (
    <motion.div
      data-testid={
        isPlaceholder
          ? `product-placeholder-${code.toLowerCase()}`
          : `product-card-${code.toLowerCase()}`
      }
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.05, ease }}
      className={`group ${clickable ? "cursor-pointer" : ""}`}
      onClick={handleClick}
      onKeyDown={handleKey}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `Open ${name || code} details` : undefined}
    >
      <div
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className={`relative aspect-[3/4] w-full overflow-hidden transition-colors duration-500 ${
          isPlaceholder
            ? "border border-dashed border-[#0a0a0c]/12 bg-[#f6f4ef] group-hover:border-[#c6a87c]/50"
            : isHardware
              ? "border border-[#0a0a0c]/8 bg-white"
              : "border border-[#0a0a0c]/8 bg-white group-hover:border-[#c6a87c]/60"
        }`}
      >
        {isPlaceholder ? (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent_0_18px,rgba(10,10,12,0.025)_18px_19px)]"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
              <span className="font-tech text-[10px] uppercase tracking-[0.4em] text-[#0a0a0c]/35">
                {categoryLabel}
              </span>
              <span className="font-serif-display text-2xl font-medium tracking-tight text-[#0a0a0c]/50 transition-colors duration-500 group-hover:text-[#c6a87c]">
                {code}
              </span>
              <span className="mt-1 font-tech text-[9px] uppercase tracking-[0.3em] text-[#0a0a0c]/25">
                Render Coming
              </span>
            </div>
          </>
        ) : has3State ? (
          <>
            {/* Rest — scene stripped, just the bare steel door (colour-tintable) */}
            <img
              src={restSrc}
              alt={name || code}
              data-testid={`product-image-rest-${code.toLowerCase()}`}
              loading="lazy"
              style={{ ...scaleStyle, ...tintStyle }}
              className="absolute inset-0 h-full w-full object-contain p-1 transition-opacity duration-[700ms] ease-[cubic-bezier(0.19,1,0.22,1)] lg:p-2"
            />
            {/* Closed with scene — fades in immediately on hover */}
            <img
              src={src}
              alt={`${name || code} — closed`}
              data-testid={`product-image-closed-${code.toLowerCase()}`}
              loading="lazy"
              style={{ ...scaleStyle, opacity: showClosed && !showOpen ? 1 : 0 }}
              className="absolute inset-0 h-full w-full object-contain p-1 transition-opacity duration-[700ms] ease-[cubic-bezier(0.19,1,0.22,1)] lg:p-2"
            />
            {/* Open — fades in shortly after hover starts (300 ms) */}
            {hoverSrc && (
              <img
                src={hoverSrc}
                alt={`${name || code} — open`}
                data-testid={`product-image-open-${code.toLowerCase()}`}
                loading="lazy"
                style={{ ...scaleStyle, opacity: showOpen ? 1 : 0 }}
                className="absolute inset-0 h-full w-full object-contain p-1 transition-opacity duration-[700ms] ease-[cubic-bezier(0.19,1,0.22,1)] lg:p-2"
              />
            )}
          </>
        ) : (
          <>
            {/* Closed / default image. Fades on hover ONLY when there is an
                open image to swap in — otherwise it must stay solid or the
                card visibly flickers (bug reported on hardware). On touch
                devices the `showOpen` state drives the same swap. */}
            <img
              src={src}
              alt={name || code}
              loading="lazy"
              style={{
                ...scaleStyle,
                ...(hoverSrc ? { opacity: showOpen ? 0 : 1 } : {}),
              }}
              className={`absolute inset-0 h-full w-full object-contain p-1 transition-opacity duration-[700ms] ease-[cubic-bezier(0.19,1,0.22,1)] lg:p-2 ${
                hoverSrc ? "group-hover:opacity-0" : ""
              }`}
            />
            {/* Open door — revealed on hover (desktop) or when in view (touch) */}
            {hoverSrc && (
              <img
                src={hoverSrc}
                alt={`${name || code} — open`}
                loading="lazy"
                style={{ ...scaleStyle, opacity: showOpen ? 1 : undefined }}
                className="absolute inset-0 h-full w-full object-contain p-1 opacity-0 transition-opacity duration-[700ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:opacity-100 lg:p-2"
              />
            )}
          </>
        )}
      </div>
      <p className="mt-3 text-center font-tech text-[11px] font-medium uppercase tracking-[0.32em] text-[#0a0a0c]/85">
        Model: {code}
      </p>
      {!isPlaceholder && name && (
        <p className="mt-1 text-center font-body text-[13px] font-medium text-[#0a0a0c]/75">
          {name}
        </p>
      )}
      {!isPlaceholder && tintable && (
        <div className="mt-3 flex flex-col items-center gap-1.5">
          <ColourSwatches
            selected={colourKey || DEFAULT_COLOUR_KEY}
            onChange={(k) => onColourChange?.(code, k)}
            onPreview={setPreviewColourKey}
            size={16}
            testIdPrefix={`swatch-${code.toLowerCase()}`}
          />
          <p
            data-testid={`swatch-${code.toLowerCase()}-label`}
            className="min-h-[14px] font-tech text-[10px] font-medium uppercase tracking-[0.3em] text-[#0a0a0c]/80"
          >
            {displayColour.label}
          </p>
        </div>
      )}
      {!isPlaceholder && subCategory === "iver" && (
        <IverOptions
          code={code}
          finishes={finishes || []}
          backplates={backplates || []}
        />
      )}
    </motion.div>
  );
};

export default function Products() {
  const navigate = useNavigate();
  const { slug: pathSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const validKeys = ["all", ...CATEGORIES.map((c) => c.key)];
  const legacyFilter = searchParams.get("filter");

  // Legacy `/products?filter=steel-windows` → canonical `/products/steel-windows`.
  // Replace (not push) so the back button still returns to the previous page.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[PROD-DEBUG] pathSlug=', pathSlug, 'legacyFilter=', legacyFilter, 'validKeys=', validKeys);
    if (!pathSlug && legacyFilter && validKeys.includes(legacyFilter)) {
      navigate(`/products/${legacyFilter}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathSlug, legacyFilter]);

  const resolvedFilter = pathSlug && validKeys.includes(pathSlug)
    ? pathSlug
    : (validKeys.includes(legacyFilter) ? legacyFilter : "all");
  const [active, setActive] = useState(resolvedFilter);
  // Sub-filter for the Hardware category: null shows both ES Signature + Iver
  // sub-groups; "signature" / "iver" narrows to just that sub-collection.
  const [hwSub, setHwSub] = useState(null);
  const [activeProduct, setActiveProduct] = useState(null);
  // Per-product Dulux Powdercoat colour selection. Keyed by product code (SD1, SW3…).
  const [colourByCode, setColourByCode] = useState({});
  const handleColourChange = (code, colourKey) =>
    setColourByCode((prev) => ({ ...prev, [code]: colourKey }));
  const allProducts = useMemo(() => buildProducts(), []);

  // Keep local state in sync when the URL changes (e.g. nav click while on page)
  useEffect(() => {
    const next = pathSlug && validKeys.includes(pathSlug)
      ? pathSlug
      : (validKeys.includes(legacyFilter) ? legacyFilter : "all");
    setActive(next);
    // Scroll to the grid so nav clicks feel like a jump-to
    if ((pathSlug || legacyFilter) && typeof window !== "undefined") {
      const grid = document.querySelector('[data-testid="products-grid"]');
      if (grid) {
        grid.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    // pathSlug / legacyFilter are the only external values; validKeys is a
    // module-level constant and setActive is a stable setState setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathSlug, legacyFilter]);

  const applyFilter = (key) => {
    setActive(key);
    // Leaving the Hardware category clears any sub-filter so returning later
    // starts at the parent view again.
    if (key !== "hardware") setHwSub(null);
    // Canonical URL shape: /products for "all", /products/:slug otherwise.
    if (key === "all") {
      navigate("/products", { replace: true });
    } else {
      navigate(`/products/${key}`, { replace: true });
    }
  };

  const applyHwSub = (sub) => {
    // Clicking the same sub twice toggles it off (returns to both sub-groups).
    setHwSub((prev) => (prev === sub ? null : sub));
    setActive("hardware");
    navigate("/products/hardware", { replace: true });
  };

  let filtered;
  if (active === "all") {
    filtered = allProducts;
  } else if (active === "hardware" && hwSub) {
    filtered = allProducts.filter(
      (p) => p.categoryKey === "hardware" && p.subCategory === hwSub,
    );
  } else {
    filtered = allProducts.filter((p) => p.categoryKey === active);
  }

  // For "All" we show group headers; for a single category we hide them.
  const showGroupHeaders = active === "all";
  const grouped = CATEGORIES.map((c) => ({
    ...c,
    items: filtered.filter((p) => p.categoryKey === c.key),
  })).filter((g) => g.items.length > 0);

  return (
    <div
      data-testid="products-page"
      className="min-h-screen bg-white text-[#0a0a0c]"
    >
      {/* Minimal white header */}
      <section className="mx-auto max-w-[1400px] px-6 pb-10 pt-40 text-center lg:px-16 lg:pt-48">
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          data-testid="products-heading"
          className="font-serif-display text-5xl font-bold uppercase leading-[0.98] tracking-tight text-[#0a0a0c] lg:text-7xl"
        >
          Products.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease }}
          className="mx-auto mt-6 max-w-xl font-body text-[14px] leading-[1.75] text-[#0a0a0c]/70 lg:text-[15px]"
        >
          A curated range of custom luxury windows and doors, engineered in
          Swiss steel, slimline steel and premium aluminium profiles to suit
          every architectural language.
        </motion.p>
      </section>

      {/* Sidebar filter + grid */}
      <section className="mx-auto max-w-[1400px] border-t border-[#0a0a0c]/10 px-6 pb-24 lg:px-16 lg:pb-32">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[240px_1fr] lg:gap-16">
          {/* Sidebar */}
          <aside
            data-testid="products-sidebar"
            className="lg:sticky lg:top-32 lg:h-fit lg:pt-12"
          >
            <nav>
              <ul className="flex flex-row flex-wrap gap-2 lg:flex-col lg:gap-0">
                <li className="lg:w-full">
                  <button
                    type="button"
                    data-testid="filter-all"
                    onClick={() => applyFilter("all")}
                    className={`inline-flex items-center gap-2 rounded-full border px-5 py-2 font-tech text-[12px] font-semibold uppercase tracking-[0.32em] transition-all duration-300 lg:w-full lg:justify-between lg:rounded-none lg:border-0 lg:border-b lg:border-[#0a0a0c]/10 lg:px-0 lg:py-4 ${
                      active === "all"
                        ? "border-[#0a0a0c] bg-[#0a0a0c] text-white lg:bg-transparent lg:text-[#c6a87c]"
                        : "border-[#0a0a0c]/15 text-[#0a0a0c] hover:border-[#c6a87c] hover:text-[#c6a87c]"
                    }`}
                  >
                    <span>All Products</span>
                  </button>
                </li>
                {CATEGORIES.map((c) => {
                  const isHardware = c.key === "hardware";
                  const isOpen = isHardware && active === "hardware";
                  return (
                    <li key={c.key} className="lg:w-full">
                      <button
                        type="button"
                        data-testid={`filter-${c.key}`}
                        onClick={() => applyFilter(c.key)}
                        aria-expanded={isHardware ? isOpen : undefined}
                        className={`inline-flex items-center gap-2 rounded-full border px-5 py-2 font-tech text-[12px] font-semibold uppercase tracking-[0.32em] transition-all duration-300 lg:w-full lg:justify-between lg:rounded-none lg:border-0 lg:border-b lg:border-[#0a0a0c]/10 lg:px-0 lg:py-4 ${
                          active === c.key
                            ? "border-[#0a0a0c] bg-[#0a0a0c] text-white lg:bg-transparent lg:text-[#c6a87c]"
                            : "border-[#0a0a0c]/15 text-[#0a0a0c] hover:border-[#c6a87c] hover:text-[#c6a87c]"
                        }`}
                      >
                        <span>{c.label}</span>
                        {isHardware && (
                          <span
                            aria-hidden
                            className={`hidden text-[10px] transition-transform duration-300 lg:inline-block ${
                              isOpen ? "rotate-180" : "rotate-0"
                            }`}
                          >
                            ▾
                          </span>
                        )}
                      </button>
                      {isHardware && isOpen && (
                        <ul
                          data-testid="filter-hardware-submenu"
                          className="mt-2 flex w-full flex-row flex-wrap gap-2 pl-2 lg:mt-0 lg:flex-col lg:gap-0 lg:border-b lg:border-[#0a0a0c]/10 lg:pl-4"
                        >
                          {[
                            { key: "signature", label: "ES Signature Range" },
                            { key: "iver", label: "Iver Range" },
                          ].map((sub) => {
                            const activeSub = hwSub === sub.key;
                            return (
                              <li key={sub.key} className="lg:w-full">
                                <button
                                  type="button"
                                  data-testid={`filter-hardware-${sub.key}`}
                                  onClick={() => applyHwSub(sub.key)}
                                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 font-tech text-[10px] font-semibold uppercase tracking-[0.28em] transition-all duration-300 lg:w-full lg:justify-between lg:rounded-none lg:border-0 lg:border-b lg:border-[#0a0a0c]/[0.06] lg:px-0 lg:py-2.5 ${
                                    activeSub
                                      ? "border-[#c6a87c] bg-[#c6a87c]/10 text-[#c6a87c] lg:bg-transparent"
                                      : "border-[#0a0a0c]/10 text-[#0a0a0c]/70 hover:border-[#c6a87c] hover:text-[#c6a87c]"
                                  }`}
                                >
                                  <span>{sub.label}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="mt-10 hidden lg:block">
              <Link
                to="/contact"
                data-testid="products-sidebar-enquire"
                className="inline-flex w-full items-center justify-center rounded-full bg-[#0a0a0c] px-6 py-3 font-tech text-[10px] uppercase tracking-[0.35em] text-white transition-all duration-500 hover:bg-[#c6a87c]"
              >
                Enquire
              </Link>
            </div>
          </aside>

          {/* Grid */}
          <div data-testid="products-grid" className="lg:pt-12">
            {grouped.length === 0 ? (
              <div className="border border-dashed border-[#0a0a0c]/15 py-24 text-center">
                <p className="font-tech text-[11px] uppercase tracking-[0.32em] text-[#0a0a0c]/50">
                  Nothing here yet.
                </p>
              </div>
            ) : (
              grouped.map((group, gi) => {
                // Hardware is split into named sub-collections; every other
                // category renders as a single flat grid.
                const isHardware = group.key === "hardware";
                const hardwareSubGroups = isHardware
                  ? [
                      {
                        key: "signature",
                        label: "ES Signature Range",
                        items: group.items.filter(
                          (p) => p.isPlaceholder || p.subCategory === "signature",
                        ),
                      },
                      {
                        key: "iver",
                        label: "Iver Range",
                        items: group.items.filter((p) => p.subCategory === "iver"),
                      },
                    ].filter((sg) => sg.items.length > 0)
                  : null;

                return (
                  <div
                    key={group.key}
                    data-testid={`products-group-${group.key}`}
                    className={gi === 0 ? "" : "mt-20"}
                  >
                    {showGroupHeaders && (
                      <div className="mb-10 flex items-baseline justify-between border-b-2 border-[#0a0a0c]/25 pb-5">
                        <h2 className="font-serif-display text-3xl font-bold uppercase tracking-tight text-[#0a0a0c] lg:text-5xl">
                          {group.label}
                        </h2>
                      </div>
                    )}

                    {isHardware && (
                      <div
                        data-testid="hardware-notice"
                        className="mb-10 flex flex-col gap-3 rounded-2xl border border-[#c6a87c]/40 bg-[#c6a87c]/[0.06] p-5 lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:p-6"
                      >
                        <div className="flex items-start gap-3">
                          <span
                            aria-hidden
                            className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#c6a87c]"
                          />
                          <p className="font-body text-[13px] leading-[1.6] text-[#0a0a0c]/80 lg:text-[14px]">
                            Prefer a different handle or hardware supplier?{" "}
                            <span className="text-[#0a0a0c]">
                              We can work with your preferred handle and hardware options
                            </span>{" "}
                            — just let us know at enquiry.
                          </p>
                        </div>
                        <div className="flex items-start gap-3 lg:border-l lg:border-[#c6a87c]/30 lg:pl-6">
                          <span
                            aria-hidden
                            className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#c6a87c]"
                          />
                          <p className="font-tech text-[11px] font-semibold uppercase tracking-[0.24em] text-[#0a0a0c]">
                            Electronic Lock integration available
                          </p>
                        </div>
                      </div>
                    )}

                    {isHardware ? (
                      hardwareSubGroups.map((sg, si) => (
                        <div
                          key={sg.key}
                          data-testid={`hardware-subgroup-${sg.key}`}
                          className={si === 0 ? "" : "mt-16"}
                        >
                          <div className="mb-8 flex items-baseline gap-4 border-b border-[#0a0a0c]/15 pb-3">
                            <h3
                              data-testid={`hardware-subgroup-heading-${sg.key}`}
                              className="font-serif-display text-xl font-bold uppercase tracking-tight text-[#0a0a0c] lg:text-2xl"
                            >
                              {sg.label}
                            </h3>
                          </div>
                          <div className="grid grid-cols-2 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-12">
                            {sg.items.map((p, i) => (
                              <ProductCard
                                key={p.code}
                                product={p}
                                index={i}
                                onOpen={setActiveProduct}
                                colourKey={colourByCode[p.code]}
                                onColourChange={handleColourChange}
                              />
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="grid grid-cols-2 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-12">
                        {group.items.map((p, i) => (
                          <ProductCard
                            key={p.code}
                            product={p}
                            index={i}
                            onOpen={setActiveProduct}
                            colourKey={colourByCode[p.code]}
                            onColourChange={handleColourChange}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Mobile enquire */}
            <div className="mt-14 lg:hidden">
              <Link
                to="/contact"
                className="inline-flex w-full items-center justify-center rounded-full bg-[#0a0a0c] px-8 py-4 font-tech text-[11px] uppercase tracking-[0.35em] text-white"
              >
                Enquire About These
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ProductsInfoSection />

      <ProductModal
        product={activeProduct}
        initialColourKey={
          activeProduct ? colourByCode[activeProduct.code] : undefined
        }
        onColourChange={(key) => {
          if (activeProduct) handleColourChange(activeProduct.code, key);
        }}
        onClose={() => setActiveProduct(null)}
      />
      <ColourFilters />
      <CustomDesignNotice />
    </div>
  );
}
